import {
  CacheableRolodexEntryJson,
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponentDataFrame,
  GeckoComponentModelType,
  GeckoInspection,
  GeckoMeasurementUnit,
  GeckoView,
} from 'types'

export function canCreateDfnCategoryLayer(
  assetModel: GeckoAssetModel | undefined,
  componentId: string,
  inspection: GeckoInspection | undefined
): boolean {
  if (assetModel?.data.type !== GeckoAssetModelType.Ship) return false
  const component = assetModel.data.components.get(componentId)
  if (component?.type !== GeckoComponentModelType.BasicShipHull) return false
  return inspection?.data.inspection_type === 'rug'
}

export function createDfnCategoryCdf(params: {
  differenceFromNominalCdf: GeckoComponentDataFrame
  assetModel: GeckoAssetModel | undefined
  inspection: GeckoInspection | undefined
}): GeckoComponentDataFrame | undefined {
  const { differenceFromNominalCdf, assetModel, inspection } = params
  const componentId = differenceFromNominalCdf.tags.component
  if (!componentId) return undefined
  if (!canCreateDfnCategoryLayer(assetModel, componentId, inspection)) {
    return undefined
  }
  if (!isPercentDifferenceFromNominalCdf(differenceFromNominalCdf)) {
    return undefined
  }

  const sourceRaw = differenceFromNominalCdf.raw
  // Source summary stats and texture describe the percent layer, not category codes.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omitted from the copied CDF
  const { stats, texture, ...usableCdfData } = sourceRaw.data
  const sourceParams = usableCdfData.params
  if (!isRecord(sourceParams)) return undefined

  const cdfEntry: CacheableRolodexEntryJson = {
    isFromCache: true,
    ...sourceRaw,
    id: `dfn-category-${differenceFromNominalCdf.id}`,
    generation: differenceFromNominalCdf.generation,
    data: {
      ...usableCdfData,
      is_default: false,
      params: {
        ...sourceParams,
        view: GeckoView.DfnCategory.id,
        measurement_unit: GeckoMeasurementUnit.DfnCategory.id,
      },
    },
  }

  return GeckoComponentDataFrame.CreateFromJSON(cdfEntry)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isPercentDifferenceFromNominalCdf(
  cdf: GeckoComponentDataFrame
): boolean {
  return (
    cdf.data.params.view.id === GeckoView.DifferenceFromNominal.id &&
    cdf.data.params.measurement_unit.id === GeckoMeasurementUnit.Percent.id
  )
}
